import React from "react";

import { InstallMobileRounded, StarRateRounded } from "@mui/icons-material";
import { Box, Chip, IconButton, Skeleton } from "@mui/material";
import { getId } from "firebase/installations";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import useAuth from "../../../../config/context/authContext";
import useFirebase from "../../../../config/context/firebaseContext";
import useMainState from "../../../../config/context/mainStateContext";
import useModal from "../../../../config/context/modalContext";
import storageKey from "../../../../config/storageKey";
import { GreyPallet } from "../../../../config/styles";
import { Dev } from "../../../../config/util/consoleUtil";
import { UtilComponents } from "../../../../config/util/utilComponents";

import MainText from "../../../../components/MainText";

export default function VersionConfig() {
  const navigate = useNavigate();

  const { currentUserState, currentUserInfo } = useAuth();
  const { forceRefreshConfigs, installations, getConfig } = useFirebase();
  const { pushAlert } = useModal();
  const { openLink } = useMainState();

  const [version] = React.useState(process.env.REACT_APP_CURRENT_VERSION || "?");
  const [installationId, setInstallationId] = React.useState("");

  const [loading, setLoading] = React.useState(false);

  const [easterEggClicks, setEasterEggClicks] = React.useState(0);

  const [bundleBuildId, setBundleBuildId] = React.useState("");

  const getManifestData = async () => {
    const randomParam = `?random=${new Date().getTime()}`;

    const response = await fetch(`/asset-manifest.json${randomParam}`, {
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }).catch(() => window.alert("Erro ao obter código atual da build!"));

    if (!response) return window.alert("Erro de conexão ao obter código atual da build!");

    console.log(...new Dev("Current getVersionInfo Response", response).Info());

    const parsedResponse = await response?.json();

    const lastModified =
      response?.headers.get("Last-Modified") || response?.headers.get("Date") || "";

    const formatedLastModified = DateTime.fromRFC2822(lastModified).toFormat("dd/LL/y HH:mm");

    console.log(...new Dev("Current Response parsedData", parsedResponse).Info());

    console.log(...new Dev("Current Response lastModify", formatedLastModified).Info());

    setBundleBuildId(
      process.env.NODE_ENV === "development"
        ? parsedResponse["files"]["main.js"].split(".")[0].split("/")[3]
        : parsedResponse["files"]["main.js"].split(".")[1]
    );
  };

  const getVersionInfo = async () => {
    try {
      setLoading(true);
      await getManifestData();
    } catch (error) {
      window.alert("Erro ao obter informações da build!");
    } finally {
      setLoading(false);
    }
  };

  const forceUpdate = () => {
    try {
      pushAlert({
        isConfirm: true,
        title: "Forçar atualização da versão?",
        onConfirm: async () => {
          await forceRefreshConfigs();

          navigator?.serviceWorker?.getRegistration().then(function (reg) {
            if (reg) {
              reg.unregister().then(function () {
                window.location.reload();
              });
            } else {
              window.location.reload();
            }
          });
        },
      });
    } catch (error) {
      window.alert("Erro ao atualizar código da build!");
    }
  };

  const getFirebaseInstallationId = async () => {
    const currentInstallationId = await getId(installations);
    setInstallationId(currentInstallationId);
  };

  const handleClickEaster = () => {
    if (easterEggClicks > 20) {
      pushAlert({
        title: "Informações da Conta",
        children: (
          <Box sx={{ whiteSpace: "break-spaces", wordWrap: "break-word" }}>
            {currentUserState ? (
              <>
                <MainText size="caption">Email:</MainText>
                <MainText size="caption" color="regular">
                  {currentUserState?.email || ""}
                </MainText>
                <MainText size="caption">ID:</MainText>
                <MainText size="caption" color="regular">
                  {currentUserState?.uid}
                </MainText>
              </>
            ) : null}
            <MainText size="caption">Token:</MainText>
            <MainText size="caption" color="regular">
              {window.localStorage.getItem(storageKey.FCM_TOKEN) || ""}
            </MainText>
          </Box>
        ),
      });
      setEasterEggClicks(0);
    } else {
      setEasterEggClicks(easterEggClicks + 1);
    }
  };

  React.useEffect(() => {
    if (!("cordova" in window)) getVersionInfo();
    getFirebaseInstallationId();
  }, []);

  return (
    <Box mt={4}>
      <Box display={"flex"} justifyContent={"space-between"}>
        <Box>
          <Box onClick={handleClickEaster}>
            <MainText size="subtitle">Versão</MainText>
          </Box>

          {"cordova" in window ? (
            <>
              <MainText size="body" color="regular">
                {version} - Você está usando a versão mobile do Circular
              </MainText>

              {String(version) < String(getConfig("latest_mobile_version")) && (
                <Box mt={2}>
                  <MainText size="body" color="black">
                    Atualização disponível
                  </MainText>
                </Box>
              )}
            </>
          ) : (
            <>
              {loading && (
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  width={"150px"}
                  height={50}
                  sx={{ borderRadius: "15px", margin: "0px 4px" }}
                />
              )}

              {bundleBuildId ? (
                <MainText size="body" color="regular">
                  {`${version} - ${bundleBuildId} ${
                    UtilComponents.isStaff(currentUserState) ? " - " + installationId : ""
                  }`}
                </MainText>
              ) : null}

              {version !== getConfig("latest_web_version") && (
                <Box mt={2}>
                  <MainText size="body" color="black">
                    Atualização Disponível
                  </MainText>
                </Box>
              )}
            </>
          )}
        </Box>

        {"cordova" in window ? (
          <Box>
            <IconButton
              color="inherit"
              onClick={() => {
                openLink("https://play.google.com/store/apps/details?id=com.circular.ufrn");
                forceRefreshConfigs();
              }}
              sx={{
                marginRight: "5px",
                "& .MuiSvgIcon-root": {
                  fontSize: "32px",
                },
              }}
            >
              <StarRateRounded />
            </IconButton>
          </Box>
        ) : (
          <Box>
            <Chip
              label={
                <Box display={"flex"} alignItems={"center"} style={{ color: GreyPallet.Regular }}>
                  <InstallMobileRounded fontSize={"small"} sx={{ mr: 1 }} />
                  <MainText size="small" color="regular">
                    Atualizar
                  </MainText>
                </Box>
              }
              variant={"filled"}
              style={{ borderWidth: 3 }}
              onClick={forceUpdate}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
